/**
 * Firebase client configuration, readable without loading the SDK.
 *
 * Deliberately separate from `firebase-app.ts`: the UI needs to know
 * whether the portal is configured in order to decide what to render, and
 * answering that question must not pull ~200 kB of Firebase SDK into the
 * bundle a visitor downloads to read the home page.
 *
 * These values are not secrets. They identify the project and are visible
 * in any client bundle. What protects the project is Firebase Security
 * Rules, the API's own authorisation, and API-key restrictions in the
 * Google Cloud console. They are read from environment variables so that
 * preview, staging and production builds point at different projects
 * without a code change.
 */

export interface FirebaseClientConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

let warned = false;

export const readFirebaseConfig = (): FirebaseClientConfig | null => {
  const env = import.meta.env;
  const config = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  };

  const missing = Object.entries(config)
    .filter(([, value]) => typeof value !== "string" || value.length === 0)
    .map(([key]) => key);

  if (missing.length > 0) {
    // Reported rather than thrown: the public pages must still render when
    // the portal is unconfigured, and the sign-in screens show an explicit
    // "not configured" state instead of a blank page.
    if (!warned) {
      warned = true;
      console.warn(
        `[growthwise] Firebase client configuration incomplete; missing ${missing.join(", ")}. ` +
          "Authentication and uploads are disabled. See .env.example.",
      );
    }
    return null;
  }

  return config as FirebaseClientConfig;
};

export const isFirebaseConfigured = (): boolean =>
  readFirebaseConfig() !== null;

/** True when the app should talk to the local emulator suite. */
export const shouldUseEmulators = (): boolean =>
  import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true";
